import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ArrowLeft, Shield } from 'lucide-react';
import Footer from '@/components/Footer';
import ContactFormModal from '@/components/ContactFormModal';

const Privacy = () => {
  const navigate = useNavigate();
  const [contactModalOpen, setContactModalOpen] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-blue-50">
      {/* Header */}
      <div className="sticky top-0 z-50 bg-white/80 backdrop-blur-md border-b border-gray-200">
        <div className="max-w-5xl mx-auto px-4 py-4 flex items-center justify-between">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate(-1)}
            className="gap-2"
          >
            <ArrowLeft className="w-4 h-4" />
            Back
          </Button>
          <div className="flex items-center gap-2">
            <Shield className="w-5 h-5 text-purple-600" />
            <span className="font-semibold text-gray-900">Privacy Policy</span>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-5xl mx-auto px-4 py-12">
        <Card className="shadow-xl">
          <CardHeader className="bg-gradient-to-r from-purple-600 to-blue-600 text-white">
            <CardTitle className="text-3xl">Privacy Policy</CardTitle>
            <p className="text-white/90 mt-2">
              Effective Date: January 1, 2026 | Last Updated: January 1, 2026
            </p>
          </CardHeader>
          
          <CardContent className="p-8 space-y-8">
            {/* Introduction */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Introduction</h2>
              <p className="text-gray-700 leading-relaxed">
                Welcome to emoQ.ai. This privacy policy explains how we collect, use, disclose, and safeguard your information when you use our platform and services. Please read this privacy policy carefully. If you do not agree with the terms of this privacy policy, please do not access the site.
              </p>
            </section>

            {/* Information We Collect */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Information We Collect</h2>
              
              <h3 className="text-xl font-semibold text-gray-800 mb-3">1. Content You Submit for Analysis</h3>
              <ul className="list-disc list-inside space-y-2 text-gray-700 mb-4">
                <li>Text content you choose to analyze</li>
                <li>Images and videos you upload (up to 90 seconds for videos)</li>
                <li>URLs and web content you submit</li>
                <li>Target audience selections and custom questions</li>
              </ul>

              <h3 className="text-xl font-semibold text-gray-800 mb-3">2. Account Information</h3>
              <ul className="list-disc list-inside space-y-2 text-gray-700 mb-4">
                <li>Email address for account creation and authentication</li>
                <li>Encrypted password for account security</li>
                <li>Authentication tokens (stored locally in your browser)</li>
                <li>Credit balance and usage history</li>
              </ul>

              <h3 className="text-xl font-semibold text-gray-800 mb-3">3. Usage Data</h3>
              <ul className="list-disc list-inside space-y-2 text-gray-700">
                <li>Analysis history and results</li>
                <li>Feature usage statistics</li>
                <li>Error logs for debugging</li>
                <li>Timestamps of analyses</li>
              </ul>
            </section>

            {/* What We Don't Collect */}
            <section className="bg-green-50 p-6 rounded-lg">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">What We DON'T Collect</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className="flex items-center gap-2">
                  <span className="text-green-600">✗</span>
                  <span className="text-gray-700">Browsing history</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-green-600">✗</span>
                  <span className="text-gray-700">Location data</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-green-600">✗</span>
                  <span className="text-gray-700">Financial information</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-green-600">✗</span>
                  <span className="text-gray-700">Health data</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-green-600">✗</span>
                  <span className="text-gray-700">Contacts or phone numbers</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-green-600">✗</span>
                  <span className="text-gray-700">Social media messages</span>
                </div>
              </div>
            </section>

            {/* How We Use Your Information */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">How We Use Your Information</h2>
              <div className="space-y-3 text-gray-700">
                <p><strong>Content Analysis:</strong> Processing your submitted content through our AI models to generate emotion scores and insights</p>
                <p><strong>Service Delivery:</strong> Providing analysis results, maintaining your account, and enabling core functionality</p>
                <p><strong>Authentication:</strong> Verifying your identity and maintaining secure sessions</p>
                <p><strong>Service Improvement:</strong> Analyzing aggregated, anonymized usage patterns to improve our AI models</p>
                <p><strong>Customer Support:</strong> Responding to inquiries and troubleshooting issues</p>
              </div>
            </section>

            {/* Data Security */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Data Security</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="flex items-start gap-3 p-4 bg-blue-50 rounded-lg">
                  <span className="text-blue-600 text-xl">✓</span>
                  <div>
                    <p className="font-semibold text-gray-900">Encryption in Transit</p>
                    <p className="text-sm text-gray-600">All data transmitted using TLS/SSL</p>
                  </div>
                </div>
                <div className="flex items-start gap-3 p-4 bg-blue-50 rounded-lg">
                  <span className="text-blue-600 text-xl">✓</span>
                  <div>
                    <p className="font-semibold text-gray-900">Encryption at Rest</p>
                    <p className="text-sm text-gray-600">Sensitive data encrypted in databases</p>
                  </div>
                </div>
                <div className="flex items-start gap-3 p-4 bg-blue-50 rounded-lg">
                  <span className="text-blue-600 text-xl">✓</span>
                  <div>
                    <p className="font-semibold text-gray-900">Access Controls</p>
                    <p className="text-sm text-gray-600">Strict employee access with MFA</p>
                  </div>
                </div>
                <div className="flex items-start gap-3 p-4 bg-blue-50 rounded-lg">
                  <span className="text-blue-600 text-xl">✓</span>
                  <div>
                    <p className="font-semibold text-gray-900">Regular Audits</p>
                    <p className="text-sm text-gray-600">Periodic security assessments</p>
                  </div>
                </div>
              </div>
            </section>

            {/* Data Sharing */}
            <section className="bg-red-50 p-6 rounded-lg">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">We Do NOT Share Your Data For:</h2>
              <ul className="space-y-2">
                <li className="flex items-center gap-2 text-gray-700">
                  <span className="text-red-600">✗</span>
                  <span>Advertising or marketing by third parties</span>
                </li>
                <li className="flex items-center gap-2 text-gray-700">
                  <span className="text-red-600">✗</span>
                  <span>Sale or rental to data brokers</span>
                </li>
                <li className="flex items-center gap-2 text-gray-700">
                  <span className="text-red-600">✗</span>
                  <span>Training AI models for other companies</span>
                </li>
                <li className="flex items-center gap-2 text-gray-700">
                  <span className="text-red-600">✗</span>
                  <span>Credit scoring or lending decisions</span>
                </li>
              </ul>
            </section>

            {/* Your Rights */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Your Rights</h2>
              <div className="space-y-3 text-gray-700">
                <p><strong>Access:</strong> View your analysis history and account information</p>
                <p><strong>Correction:</strong> Update your email, password, and preferences</p>
                <p><strong>Deletion:</strong> Delete individual analyses or your entire account</p>
                <p><strong>Portability:</strong> Export your data in JSON format</p>
                <p><strong>Opt-Out:</strong> Unsubscribe from marketing emails</p>
              </div>
            </section>

            {/* GDPR & CCPA */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">GDPR & CCPA Compliance</h2>
              <p className="text-gray-700 mb-4">
                For users in the European Economic Area and California, we comply with GDPR and CCPA regulations. You have additional rights including:
              </p>
              <ul className="list-disc list-inside space-y-2 text-gray-700">
                <li>Right to know what data we collect</li>
                <li>Right to deletion ("right to be forgotten")</li>
                <li>Right to data portability</li>
                <li>Right to opt-out of data sharing</li>
                <li>Right to non-discrimination</li>
                <li>Right to lodge a complaint with authorities</li>
              </ul>
            </section>

            {/* Contact */}
            <section className="bg-purple-50 p-6 rounded-lg">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Contact Us</h2>
              <div className="space-y-4 text-gray-700">
                <div className="bg-white p-4 rounded-lg border-2 border-purple-200">
                  <p className="font-semibold text-purple-600 mb-2">📧 All Support & Privacy Questions</p>
                  <p className="mb-3">For any inquiries, support requests, or privacy-related questions, please use our contact form:</p>
                  <Button
                    onClick={() => setContactModalOpen(true)}
                    className="bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white"
                  >
                    🛡️ Submit Privacy Request
                  </Button>
                </div>
                
                <div className="bg-blue-50 p-4 rounded-lg">
                  <p className="text-sm"><strong>For General Inquiries:</strong> Use the contact form and select "Support"</p>
                  <p className="text-sm"><strong>For Privacy Rights Requests:</strong> Use the contact form and include "Privacy Rights Request" in your subject</p>
                  <p className="text-sm"><strong>For GDPR/CCPA Requests:</strong> Use the contact form and include "GDPR" or "CCPA" in your message</p>
                </div>
                
                <p className="text-sm text-gray-600 mt-4">
                  <strong>Response Time:</strong> 5-10 business days for general inquiries, 30 days for formal data rights requests
                </p>
              </div>
            </section>

            {/* Changes to Policy */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Changes to This Policy</h2>
              <p className="text-gray-700">
                We may update this privacy policy from time to time. When we make material changes, we will notify you via email and display a notice in the application. Your continued use after changes constitutes acceptance of the updated policy.
              </p>
            </section>

            {/* Last Updated */}
            <section className="text-center pt-6 border-t border-gray-200">
              <p className="text-sm text-gray-500">
                Version 1.0 | Policy ID: EMOQ-PP-2026-01
              </p>
              <p className="text-sm text-gray-500 mt-2">
                © {new Date().getFullYear()} emoQ.ai. All rights reserved.
              </p>
            </section>
          </CardContent>
        </Card>
      </div>

      {/* Footer */}
      <Footer darkMode={false} />

      {/* Contact Form Modal */}
      <ContactFormModal 
        isOpen={contactModalOpen}
        onClose={() => setContactModalOpen(false)}
        type="privacy"
      />
    </div>
  );
};

export default Privacy;
